import type React from "react";
import { useState } from "react";

// Widget for a paused EHR workflow that needs a clinical decision mid-flight
// (e.g. "which of these 3 matching lab tests?"). Payload-driven and
// EHR-agnostic — the backend decides the copy and which workflow runs; this
// component only collects answers. INPUT MODE ONLY: a paused WorkflowRun sends
// `questions`, the clinician answers, and submit dispatches the
// `workflow_respond` custom action (keyed by run_id). On submit the widget
// collapses to a compact "On it…" chip and hands the conversation back to the
// thread, which narrates the placement PROGRESS as assistant messages.
export interface WorkflowQuestion {
  key: string;
  prompt: string;
  options?: string[];
  // When true, the clinician can pick several options (e.g. a panel's tests);
  // the answer for this key is sent as a string[]. Defaults to all options.
  multi?: boolean;
}

type Answer = string | string[];

export interface WorkflowPromptData {
  run_id?: string | null;
  title?: string;
  ehr_label?: string;
  question?: string;
  questions?: WorkflowQuestion[];
}

interface WorkflowPromptProps {
  data: WorkflowPromptData;
  onCustomAction: (action: { type: string; payload?: Record<string, unknown> }) => void;
  disabled?: boolean;
}

// The widget only kicks the resume off; progress + outcome stream into the thread.
type Phase = { kind: "input"; questions: WorkflowQuestion[] } | { kind: "submitted" };

const BoltIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <path d="M8.5 1.5 3 9h4l-.5 5.5L13 7H8.5l0-5.5Z" stroke="#6366F1" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

// Options are encoded `<value>|<human label>`: the value (segment 0) is what
// the backend parses back off the answer; the label (everything after the
// first `|`) is what the clinician should see — never the raw id. Plain
// options with no `|` (e.g. an AMD panel's "CBC") display as-is.
const optionLabel = (opt: string): string => {
  const idx = opt.indexOf("|");
  return idx === -1 ? opt : opt.slice(idx + 1).trim() || opt;
};

const defaultsFor = (questions: WorkflowQuestion[]): Record<string, Answer> =>
  Object.fromEntries(
    // Multi defaults to ALL options selected (the recommendation implies the
    // whole set); single defaults to the first option.
    questions.map((q) => [q.key, q.multi ? [...(q.options ?? [])] : q.options?.[0] ?? ""]),
  );

const WorkflowPrompt: React.FC<WorkflowPromptProps> = ({ data, onCustomAction, disabled }) => {
  const initialQuestions = data?.questions || [];
  const [phase, setPhase] = useState<Phase>({ kind: "input", questions: initialQuestions });
  const [answers, setAnswers] = useState<Record<string, Answer>>(() => defaultsFor(initialQuestions));

  if (!data?.run_id || !data?.questions?.length) return null;

  const submitAnswers = () => {
    if (disabled) return;
    onCustomAction({
      type: "workflow_respond",
      payload: { run_id: data.run_id, answers },
    });
    setPhase({ kind: "submitted" });
  };

  // --- collapsed chip after submit -----------------------------------------
  if (phase.kind === "submitted") {
    return (
      <div style={{ ...styles.container, ...styles.resolved }}>
        <div style={styles.header}>
          <BoltIcon />
          <span style={styles.headerText}>On it — I'll keep you posted in the chat.</span>
        </div>
      </div>
    );
  }

  // --- input mode: answer the clinical decision ----------------------------
  const { questions } = phase;
  const answered = (q: WorkflowQuestion): boolean => {
    const v = answers[q.key];
    return q.multi ? Array.isArray(v) && v.length > 0 : (v ?? "").toString().trim() !== "";
  };
  const toggleMulti = (key: string, opt: string) =>
    setAnswers((p) => {
      const cur = Array.isArray(p[key]) ? (p[key] as string[]) : [];
      return { ...p, [key]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
    });
  const allAnswered = questions.every(answered);

  // The header already shows the question (the backend sets `question` to the
  // first prompt), so don't repeat it as a per-row label below — that's the
  // duplicated "subtitle". Only render a row's prompt when it differs.
  const headerText = (data.question || data.title || "").trim();

  return (
    <div style={styles.container}>
      {(data.title || data.question) && (
        <div style={styles.header}>
          <BoltIcon />
          <span style={styles.headerText}>{data.question || data.title}</span>
        </div>
      )}
      {questions.map((q) => (
        <div key={q.key} style={styles.qRow}>
          {q.prompt.trim() !== headerText && <span style={styles.prompt}>{q.prompt}</span>}
          {q.multi && q.options?.length ? (
            <div style={styles.checkGroup}>
              {q.options.map((opt) => (
                <label key={opt} style={styles.checkRow}>
                  <input
                    type="checkbox"
                    disabled={disabled}
                    checked={Array.isArray(answers[q.key]) && (answers[q.key] as string[]).includes(opt)}
                    onChange={() => toggleMulti(q.key, opt)}
                  />
                  <span>{optionLabel(opt)}</span>
                </label>
              ))}
            </div>
          ) : q.options?.length ? (
            <div style={styles.radioGroup}>
              {q.options.map((opt) => (
                <label key={opt} style={styles.checkRow}>
                  <input
                    type="radio"
                    name={q.key}
                    disabled={disabled}
                    checked={(answers[q.key] as string) === opt}
                    onChange={() => setAnswers((p) => ({ ...p, [q.key]: opt }))}
                  />
                  <span>{optionLabel(opt)}</span>
                </label>
              ))}
            </div>
          ) : (
            <input
              type="text"
              disabled={disabled}
              value={(answers[q.key] as string) ?? ""}
              onChange={(e) => setAnswers((p) => ({ ...p, [q.key]: e.target.value }))}
              style={styles.input}
            />
          )}
        </div>
      ))}
      <button
        type="button"
        disabled={disabled || !allAnswered}
        onClick={submitAnswers}
        style={{
          ...styles.button,
          ...styles.confirmButton,
          ...(disabled || !allAnswered ? styles.buttonDisabled : {}),
        }}
      >
        Confirm &amp; continue
      </button>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px 14px",
    marginTop: "8px",
    borderRadius: "12px",
    border: "1px solid #C7D2FE",
    backgroundColor: "#F5F3FF",
  },
  resolved: { backgroundColor: "#F0FDF4", border: "1px solid #BBF7D0" },
  header: { display: "flex", alignItems: "center", gap: "8px" },
  headerText: { fontSize: "13px", fontWeight: 600, color: "#111827", lineHeight: 1.4 },
  qRow: { display: "flex", flexDirection: "column", gap: "5px", fontSize: "13px" },
  prompt: { color: "#111827", fontWeight: 500 },
  checkGroup: { display: "flex", flexDirection: "column", gap: "4px", paddingLeft: "2px" },
  radioGroup: { display: "flex", flexDirection: "column", gap: "4px", paddingLeft: "2px" },
  checkRow: { display: "flex", alignItems: "center", gap: "7px", fontSize: "13px", color: "#374151" },
  input: {
    padding: "6px 8px",
    border: "1px solid #D1D5DB",
    borderRadius: "8px",
    fontFamily: "inherit",
    fontSize: "13px",
  },
  button: {
    flex: "0 1 auto",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "8px",
    border: "1px solid transparent",
    cursor: "pointer",
    fontFamily: "inherit",
    marginTop: "4px",
  },
  confirmButton: { backgroundColor: "#4338CA", color: "#FFFFFF", borderColor: "#4338CA" },
  buttonDisabled: { opacity: 0.6, cursor: "not-allowed" },
};

export default WorkflowPrompt;
